import React, { useCallback, useRef } from 'react';
import { Box, Grid, Autocomplete, Typography, InputAdornment } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { usePermission } from '../../../hooks/usePermission';
import { InventoryFilterState } from '../../../types/inventory';
import { useRangeFilter } from '../../../hooks/useRangeFilter';
import { useInventoryQueryFilters } from './InventoryQueryFilters.hooks';
import { RangeNumberInput, type RangeNumberInputHandle } from '../../common/RangeNumberInput';
import CatalogAutocomplete from '../../common/CatalogAutocomplete';
import {
  ListPageFilterContainer as FiltersContainer,
  ListPagePrimaryFilterRow,
  ListPageActiveFilters,
  LocationScopeControl,
} from '../../common/list-page';
import {
  StandardTextField,
  TransferButton,
  MoreFiltersContainer,
} from './InventoryQueryFilters.styles';
import {
  WeightSliderContainer,
  StyledSlider,
  WeightUnit,
} from '../../products/ProductFilters/ProductFilters.styles';

export interface InventoryQueryFiltersProps {
  categories: { id: number; name: string }[];
  locations: { id: number; name: string }[];
  statuses: { id: string; name: string }[];
  tags?: { id: number; name: string }[];
  colors?: { id: number; name: string }[];
  materials?: { id: number; name: string }[];
  onFilterChange: (filters: InventoryFilterState) => void;
  initialFilters?: InventoryFilterState;
  selectedItems?: string[];
  onTransferClick?: () => void;
  onRequestClick?: () => void;
  /** Enter en "Escanear código de barras" (Más filtros): selecciona pieza para transferencia. */
  onScanSubmit?: (code: string) => void | Promise<void>;
  isInventoryBranchQuery?: boolean;
  /** Gestión en almacén: chip fijo "Almacén", sin filtro de ubicación en Más. */
  isInventoryWarehouseQuery?: boolean;
  isLocationLocked?: boolean;
  /** Mini confirmación al cambiar alcance (gestión en sucursal). */
  onLocationScopeChange?: (payload: { locationId: string; locationName: string }) => void;
  rightControls?: React.ReactNode;
  /** Solo gestión en almacén (por ahora): sin placeholders en filtros secundarios; Transferir junto a Buscar y toggles de vista después. */
  hideSecondaryFilterPlaceholders?: boolean;
  filterVariant?: 'default' | 'general';
  /** Sliders de peso y precio de venta (inventario general, gestión almacén/sucursal). */
  showPieceNumericFilters?: boolean;
  warehousePlacementStatuses?: { id: string; name: string }[];
  branchAvailabilityStatuses?: { id: string; name: string }[];
}

const DEFAULT_WEIGHT_SLIDER_MAX = 100;
const ABSOLUTE_WEIGHT_SLIDER_MAX = 1000;
const DEFAULT_PRICE_SLIDER_MAX = 1000;
const ABSOLUTE_PRICE_SLIDER_MAX = 500000;
const DEFAULT_SALE_PRICE_PER_GRAM_MAX = 30;
const ABSOLUTE_SALE_PRICE_PER_GRAM_MAX = 500;

const InventoryQueryFilters: React.FC<InventoryQueryFiltersProps> = ({
  categories = [],
  locations = [],
  statuses = [],
  tags = [],
  colors = [],
  materials = [],
  onFilterChange,
  initialFilters,
  selectedItems = [],
  onTransferClick,
  onRequestClick,
  onScanSubmit,
  isInventoryBranchQuery = false,
  isInventoryWarehouseQuery = false,
  isLocationLocked = false,
  onLocationScopeChange,
  rightControls,
  hideSecondaryFilterPlaceholders = false,
  filterVariant = 'default',
  showPieceNumericFilters = filterVariant === 'general',
  warehousePlacementStatuses = [],
  branchAvailabilityStatuses = [],
}) => {
  const {
    filters,
    showMoreFilters,
    barcodeInput,
    activeFiltersCount,
    activeFilterChips,
    hasSelectedItems,
    handleChange,
    handleKeyDown,
    handleBarcodeInputChange,
    handleBarcodeScanKeyDown,
    handleReset,
    handleToggleMoreFilters,
    handleSearch,
    handleTransfer,
    handleRequest,
    mergeAndCommit,
    handleTagsChange,
    handleColorsChange,
    handleStatusesChange,
    handleLocationsChange,
    handleLocationScopeChange,
    handleMaterialChange,
    handleWarehousePlacementChange,
    handleBranchAvailabilityChange,
    handlePieceNumericFiltersPatch,
    handlePieceNumericFiltersSearch,
    selectedTags,
    selectedColor,
    selectedStatuses,
    selectedLocations,
    selectedMaterial,
    selectedWarehousePlacement,
    selectedBranchAvailability,
  } = useInventoryQueryFilters({
    categories,
    locations,
    statuses,
    tags,
    colors,
    materials,
    warehousePlacementStatuses,
    branchAvailabilityStatuses,
    onFilterChange,
    initialFilters,
    selectedItems,
    onTransferClick,
    onRequestClick,
    onScanSubmit,
    isInventoryBranchQuery,
    isInventoryWarehouseQuery,
    isLocationLocked,
    onLocationScopeChange,
  });

  const weightMinRef = useRef<RangeNumberInputHandle>(null);
  const weightMaxRef = useRef<RangeNumberInputHandle>(null);
  const priceMinRef = useRef<RangeNumberInputHandle>(null);
  const priceMaxRef = useRef<RangeNumberInputHandle>(null);
  const pricePerGramMinRef = useRef<RangeNumberInputHandle>(null);
  const pricePerGramMaxRef = useRef<RangeNumberInputHandle>(null);

  const onWeightRangePatch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersPatch({
        weightGramsMin: patch.min,
        weightGramsMax: patch.max,
      }),
    [handlePieceNumericFiltersPatch],
  );

  const onWeightRangeSearch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersSearch({
        weightGramsMin: patch.min,
        weightGramsMax: patch.max,
      }),
    [handlePieceNumericFiltersSearch],
  );

  const onPriceRangePatch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersPatch({
        salePriceMin: patch.min,
        salePriceMax: patch.max,
      }),
    [handlePieceNumericFiltersPatch],
  );

  const onPriceRangeSearch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersSearch({
        salePriceMin: patch.min,
        salePriceMax: patch.max,
      }),
    [handlePieceNumericFiltersSearch],
  );

  const onPricePerGramRangePatch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersPatch({
        salePricePerGramMin: patch.min,
        salePricePerGramMax: patch.max,
      }),
    [handlePieceNumericFiltersPatch],
  );

  const onPricePerGramRangeSearch = useCallback(
    (patch: { min: number | undefined; max: number | undefined }) =>
      handlePieceNumericFiltersSearch({
        salePricePerGramMin: patch.min,
        salePricePerGramMax: patch.max,
      }),
    [handlePieceNumericFiltersSearch],
  );

  const weightRange = useRangeFilter({
    min: filters.weightGramsMin,
    max: filters.weightGramsMax,
    defaultMax: DEFAULT_WEIGHT_SLIDER_MAX,
    absoluteMax: ABSOLUTE_WEIGHT_SLIDER_MAX,
    onPatch: onWeightRangePatch,
    onSearchWithPatch: onWeightRangeSearch,
  });

  const priceRange = useRangeFilter({
    min: filters.salePriceMin,
    max: filters.salePriceMax,
    defaultMax: DEFAULT_PRICE_SLIDER_MAX,
    absoluteMax: ABSOLUTE_PRICE_SLIDER_MAX,
    onPatch: onPriceRangePatch,
    onSearchWithPatch: onPriceRangeSearch,
  });

  const pricePerGramRange = useRangeFilter({
    min: filters.salePricePerGramMin,
    max: filters.salePricePerGramMax,
    defaultMax: DEFAULT_SALE_PRICE_PER_GRAM_MAX,
    absoluteMax: ABSOLUTE_SALE_PRICE_PER_GRAM_MAX,
    onPatch: onPricePerGramRangePatch,
    onSearchWithPatch: onPricePerGramRangeSearch,
  });

  const commitAllRangeInputs = useCallback(() => {
    weightMinRef.current?.commit();
    weightMaxRef.current?.commit();
    priceMinRef.current?.commit();
    priceMaxRef.current?.commit();
    pricePerGramMinRef.current?.commit();
    pricePerGramMaxRef.current?.commit();
  }, []);

  const handleSearchWithRanges = useCallback(() => {
    commitAllRangeInputs();
    handleSearch();
  }, [commitAllRangeInputs, handleSearch]);

  const canTransferPermission = usePermission(
    isInventoryBranchQuery
      ? 'inventory.inventory-branch.transfer'
      : 'inventory.inventory-warehouse.transfer',
  );

  const canRequestPermission = usePermission('inventory.inventory-general.create');

  const handleKeyDownWithRanges = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        commitAllRangeInputs();
      }
      handleKeyDown(e);
    },
    [commitAllRangeInputs, handleKeyDown],
  );

  const stripSecondaryPh = hideSecondaryFilterPlaceholders;
  const transferButton =
    canTransferPermission && onTransferClick ? (
      <TransferButton
        variant="contained"
        size="small"
        onClick={handleTransfer}
        disabled={!hasSelectedItems}
        startIcon={<SendIcon />}
        sx={{
          minWidth: 120,
          background: 'linear-gradient(to right, #06b6d4, #10b981)',
          color: '#ffffff',
          '&:hover:not(.Mui-disabled)': {
            background: 'linear-gradient(to right, #0891b2, #059669)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.85)',
            opacity: 0.55,
          },
        }}
      >
        Transferir ({selectedItems.length})
      </TransferButton>
    ) : null;

  const requestButton =
    canRequestPermission && onRequestClick ? (
      <TransferButton
        variant="contained"
        size="small"
        onClick={handleRequest}
        disabled={!hasSelectedItems}
        startIcon={<SendIcon />}
        sx={{
          minWidth: 120,
          background: 'linear-gradient(to right, #6366f1, #8b5cf6)',
          color: '#ffffff',
          '&:hover:not(.Mui-disabled)': {
            background: 'linear-gradient(to right, #4f46e5, #7c3aed)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.85)',
            opacity: 0.55,
          },
        }}
      >
        Solicitar ({selectedItems.length})
      </TransferButton>
    ) : null;

  return (
    <FiltersContainer
      sx={
        hideSecondaryFilterPlaceholders
          ? (theme) => ({
              paddingTop: theme.spacing(1.5),
              paddingBottom: theme.spacing(1),
            })
          : undefined
      }
    >
      <ListPagePrimaryFilterRow
        showMoreFilters={showMoreFilters}
        onToggleMoreFilters={handleToggleMoreFilters}
        onReset={handleReset}
        onSearch={handleSearchWithRanges}
        moreFiltersActiveCount={activeFiltersCount}
        betweenResetAndSearch={
          isInventoryWarehouseQuery ? (
            <LocationScopeControl
              fixedLabel="Almacén"
              iconVariant="warehouse"
              lockedTooltip="Solo inventario de almacén"
            />
          ) : isInventoryBranchQuery ? (
            <LocationScopeControl
              locations={locations.filter((loc) => loc.id !== 0)}
              value={filters.locationId || ''}
              locked={isLocationLocked}
              onChange={handleLocationScopeChange}
            />
          ) : null
        }
        searchFieldProps={{
          label: 'Buscar pieza',
          name: 'searchTerm',
          value: filters.searchTerm || '',
          onChange: handleChange,
          onKeyDown: handleKeyDownWithRanges,
        }}
        trailingActions={
          <>
            {transferButton}
            {requestButton}
            {rightControls}
          </>
        }
      />

      {/* Segunda fila: Filtros adicionales (se despliega al hacer clic en "Más") */}
      <MoreFiltersContainer
        in={showMoreFilters}
        sx={
          hideSecondaryFilterPlaceholders
            ? {
                marginTop: 0,
                '& .MuiCollapse-wrapper': { paddingTop: 0 },
              }
            : undefined
        }
      >
        <Grid
          container
          spacing={2}
          sx={
            hideSecondaryFilterPlaceholders
              ? (theme) => ({
                  paddingTop: theme.spacing(3),
                })
              : undefined
          }
        >
          {onScanSubmit && canTransferPermission ? (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <StandardTextField
                fullWidth
                size="small"
                label="Escanear código de barras"
                value={barcodeInput}
                onChange={handleBarcodeInputChange}
                onKeyDown={handleBarcodeScanKeyDown}
                placeholder="Escanea la pieza o escribe el código completo y pulsa Enter"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeScannerIcon sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          ) : null}

          {/* Estado(s) del inventario */}
          {filterVariant === 'general' ? (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  options={warehousePlacementStatuses}
                  getOptionLabel={(option) => option.name}
                  value={selectedWarehousePlacement}
                  onChange={handleWarehousePlacementChange}
                  size="small"
                  noOptionsText="Sin coincidencias"
                  renderInput={(params) => (
                    <StandardTextField
                      {...params}
                      fullWidth
                      label="Estado de inventario almacén"
                      placeholder={stripSecondaryPh ? '' : 'Seleccionar'}
                      inputProps={{
                        ...params.inputProps,
                        ...(stripSecondaryPh ? { placeholder: '' } : {}),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  options={branchAvailabilityStatuses}
                  getOptionLabel={(option) => option.name}
                  value={selectedBranchAvailability}
                  onChange={handleBranchAvailabilityChange}
                  size="small"
                  noOptionsText="Sin coincidencias"
                  renderInput={(params) => (
                    <StandardTextField
                      {...params}
                      fullWidth
                      label="Estado de inventario sucursal"
                      placeholder={stripSecondaryPh ? '' : 'Seleccionar'}
                      inputProps={{
                        ...params.inputProps,
                        ...(stripSecondaryPh ? { placeholder: '' } : {}),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                    },
                  }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={statuses}
                getOptionLabel={(option) => option.name}
                value={selectedStatuses}
                onChange={handleStatusesChange}
                size="small"
                noOptionsText="Sin coincidencias"
                renderInput={(params) => (
                  <StandardTextField
                    {...params}
                    fullWidth
                    label={isInventoryBranchQuery ? 'Estado de Venta' : 'Estado del Inventario'}
                    placeholder={stripSecondaryPh ? '' : 'Seleccionar estado'}
                    inputProps={{
                      ...params.inputProps,
                      ...(stripSecondaryPh ? { placeholder: '' } : {}),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: '200px',
                  },
                }}
              />
            </Grid>
          )}

          {/* Ubicación (alcance vive en la fila principal en almacén/sucursal) */}
          {!isInventoryBranchQuery && !isInventoryWarehouseQuery ? (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name}
                value={selectedLocations}
                onChange={handleLocationsChange}
                size="small"
                noOptionsText="Sin coincidencias"
                disabled={isLocationLocked}
                sx={
                  isLocationLocked
                    ? {
                        '& .MuiAutocomplete-inputRoot': {
                          backgroundColor: '#fff',
                        },
                        '& .MuiInputBase-root.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.87)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                          opacity: 1,
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.87)',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                          opacity: 1,
                        },
                        '& .MuiInputLabel-root.Mui-disabled': {
                          color: 'text.secondary',
                          opacity: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'grey.300',
                        },
                      }
                    : undefined
                }
                renderInput={(params) => (
                  <StandardTextField
                    {...params}
                    fullWidth
                    label="Ubicación"
                    placeholder={stripSecondaryPh ? '' : 'Seleccionar ubicación'}
                    inputProps={{
                      ...params.inputProps,
                      ...(stripSecondaryPh ? { placeholder: '' } : {}),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: '200px',
                  },
                }}
              />
            </Grid>
          ) : null}

          {/* Tipo de producto */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <CatalogAutocomplete
              catalogType="category"
              value={filters.categoryName}
              valueMode="name"
              onChange={(value) => mergeAndCommit({ categoryName: value })}
              pinOptions={categories}
              label="Tipo de Producto"
              placeholder={stripSecondaryPh ? '' : 'Seleccionar tipo'}
              size="small"
              textFieldProps={{
                fullWidth: true,
                inputProps: stripSecondaryPh ? { placeholder: '' } : undefined,
              }}
            />
          </Grid>

          {/* Número de registro */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StandardTextField
              fullWidth
              label="Número de Registro"
              name="recordCode"
              value={filters.recordCode || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDownWithRanges}
              size="small"
              placeholder={stripSecondaryPh ? undefined : 'Ej: REG-2025-001'}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Etiqueta */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              multiple
              options={tags}
              getOptionLabel={(option) => option.name}
              value={selectedTags}
              onChange={handleTagsChange}
              size="small"
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <StandardTextField
                  {...params}
                  fullWidth
                  label="Etiqueta"
                  placeholder={stripSecondaryPh ? '' : 'Seleccionar etiquetas'}
                  inputProps={{
                    ...params.inputProps,
                    ...(stripSecondaryPh ? { placeholder: '' } : {}),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: '200px',
                },
              }}
            />
          </Grid>

          {/* Fecha de inicio */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StandardTextField
              fullWidth
              label={isInventoryBranchQuery ? 'Ingreso desde' : 'Fecha de Inicio'}
              name="createdAtStart"
              type="date"
              value={filters.createdAtStart || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDownWithRanges}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Fecha de fin */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StandardTextField
              fullWidth
              label={isInventoryBranchQuery ? 'Ingreso hasta' : 'Fecha de Fin'}
              name="createdAtEnd"
              type="date"
              value={filters.createdAtEnd || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDownWithRanges}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Material */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              options={materials}
              getOptionLabel={(option) => option.name}
              value={selectedMaterial}
              onChange={handleMaterialChange}
              size="small"
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <StandardTextField
                  {...params}
                  fullWidth
                  label="Material"
                  placeholder={stripSecondaryPh ? '' : 'Seleccionar material'}
                  inputProps={{
                    ...params.inputProps,
                    ...(stripSecondaryPh ? { placeholder: '' } : {}),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: '200px',
                },
              }}
            />
          </Grid>

          {/* Talla */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StandardTextField
              fullWidth
              label="Talla"
              name="diameterSize"
              value={filters.diameterSize || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDownWithRanges}
              size="small"
              placeholder={stripSecondaryPh ? undefined : 'Ej: 42, XL, 10mm'}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Color */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              options={colors}
              getOptionLabel={(option) => option.name}
              value={selectedColor}
              onChange={handleColorsChange}
              size="small"
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <StandardTextField
                  {...params}
                  fullWidth
                  label="Color"
                  placeholder={stripSecondaryPh ? '' : 'Seleccionar color'}
                  inputProps={{
                    ...params.inputProps,
                    ...(stripSecondaryPh ? { placeholder: '' } : {}),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: '200px',
                },
              }}
            />
          </Grid>

          {/* Código Proveedor */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <StandardTextField
              fullWidth
              label="Código Proveedor"
              name="workshopCode"
              value={filters.workshopCode || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDownWithRanges}
              size="small"
              placeholder={stripSecondaryPh ? undefined : 'Ej: WS001'}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {showPieceNumericFilters && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  flexWrap: 'nowrap',
                  gap: { xs: 2, md: 2 },
                  width: '100%',
                  alignItems: 'stretch',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    flex: { xs: '1 1 auto', md: '1 1 0' },
                    width: { xs: '100%', md: 'auto' },
                    minWidth: { md: 220 },
                    maxWidth: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Peso de la pieza (g)
                  </Typography>
                  <WeightSliderContainer sx={{ width: '100%' }}>
                    <RangeNumberInput
                      ref={weightMinRef}
                      value={filters.weightGramsMin}
                      defaultDisplayValue={0}
                      onCommit={(v) => weightRange.applyMin(v, false)}
                      onEnter={(v) => weightRange.applyMin(v, true)}
                      max={ABSOLUTE_WEIGHT_SLIDER_MAX}
                    />
                    <StyledSlider
                      value={weightRange.sliderTemp}
                      onChange={weightRange.onSliderChange}
                      onChangeCommitted={weightRange.onSliderCommitted}
                      valueLabelDisplay="auto"
                      min={0}
                      max={weightRange.sliderAxisMax}
                      step={1}
                    />
                    <RangeNumberInput
                      ref={weightMaxRef}
                      value={filters.weightGramsMax}
                      defaultDisplayValue={DEFAULT_WEIGHT_SLIDER_MAX}
                      onCommit={(v) => weightRange.applyMax(v, false)}
                      onEnter={(v) => weightRange.applyMax(v, true)}
                      max={ABSOLUTE_WEIGHT_SLIDER_MAX}
                    />
                    <WeightUnit>g</WeightUnit>
                  </WeightSliderContainer>
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 auto', md: '1 1 0' },
                    width: { xs: '100%', md: 'auto' },
                    minWidth: { md: 240 },
                    maxWidth: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Precio de Venta (Pieza)
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      '& input': { width: 'min(72px, 100%)' },
                    }}
                  >
                    <WeightSliderContainer sx={{ width: '100%' }}>
                      <RangeNumberInput
                        ref={priceMinRef}
                        value={filters.salePriceMin}
                        defaultDisplayValue={0}
                        onCommit={(v) => priceRange.applyMin(v, false)}
                        onEnter={(v) => priceRange.applyMin(v, true)}
                        max={ABSOLUTE_PRICE_SLIDER_MAX}
                      />
                      <StyledSlider
                        value={priceRange.sliderTemp}
                        onChange={priceRange.onSliderChange}
                        onChangeCommitted={priceRange.onSliderCommitted}
                        valueLabelDisplay="auto"
                        min={0}
                        max={priceRange.sliderAxisMax}
                        step={5}
                      />
                      <RangeNumberInput
                        ref={priceMaxRef}
                        value={filters.salePriceMax}
                        defaultDisplayValue={DEFAULT_PRICE_SLIDER_MAX}
                        onCommit={(v) => priceRange.applyMax(v, false)}
                        onEnter={(v) => priceRange.applyMax(v, true)}
                        max={ABSOLUTE_PRICE_SLIDER_MAX}
                      />
                      <WeightUnit>$</WeightUnit>
                    </WeightSliderContainer>
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 auto', md: '1 1 0' },
                    width: { xs: '100%', md: 'auto' },
                    minWidth: { md: 240 },
                    maxWidth: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Precio de Venta por Gramo
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      '& input': { width: 'min(72px, 100%)' },
                    }}
                  >
                    <WeightSliderContainer sx={{ width: '100%' }}>
                      <RangeNumberInput
                        ref={pricePerGramMinRef}
                        value={filters.salePricePerGramMin}
                        defaultDisplayValue={0}
                        onCommit={(v) => pricePerGramRange.applyMin(v, false)}
                        onEnter={(v) => pricePerGramRange.applyMin(v, true)}
                        max={ABSOLUTE_SALE_PRICE_PER_GRAM_MAX}
                        allowDecimals
                        decimalPlaces={2}
                      />
                      <StyledSlider
                        value={pricePerGramRange.sliderTemp}
                        onChange={pricePerGramRange.onSliderChange}
                        onChangeCommitted={pricePerGramRange.onSliderCommitted}
                        valueLabelDisplay="auto"
                        min={0}
                        max={pricePerGramRange.sliderAxisMax}
                        step={0.05}
                      />
                      <RangeNumberInput
                        ref={pricePerGramMaxRef}
                        value={filters.salePricePerGramMax}
                        defaultDisplayValue={DEFAULT_SALE_PRICE_PER_GRAM_MAX}
                        onCommit={(v) => pricePerGramRange.applyMax(v, false)}
                        onEnter={(v) => pricePerGramRange.applyMax(v, true)}
                        max={ABSOLUTE_SALE_PRICE_PER_GRAM_MAX}
                        allowDecimals
                        decimalPlaces={2}
                      />
                      <WeightUnit>Bs/g</WeightUnit>
                    </WeightSliderContainer>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </MoreFiltersContainer>

      <ListPageActiveFilters chips={activeFilterChips} />
    </FiltersContainer>
  );
};

export default InventoryQueryFilters;
